/**
 * HlsEventBus — single shared SSE consumer for /api/hls/events.
 *
 * Mirrors core/ops_bus.js exactly (same subscribe/unsub API, same
 * recent-events ring buffer, same auto-reconnect). Subscribed to a
 * dedicated zapd HLS SSE endpoint, which is fed by the same
 * Node service (zap-ops.service) but a separate ioredis subscriber on
 * Redis channel `zap:hls:events`.
 *
 * The Node bridge applies a server-side privacy filter per connection:
 *   - own-session events arrive with full `media_basename` + `state_detail`
 *   - foreign-session events arrive with those two fields nulled
 *   - admin clients see everything
 *
 * Frontend therefore does NOT need to do its own privacy filtering — and
 * cannot be tricked via DevTools into reading data the server didn't send.
 *
 * Two consumers:
 *   - ZapPlayer (filters by own session_hash to drive phase UI)
 *   - HlsLiveBadge (renders all sessions in the sidebar drawer)
 */

const RATE_WINDOW_MS = 60_000;
const RECENT_EVENTS_CAP = 200;

class HlsEventBus {
  constructor() {
    this.es = null;
    this.subscribers = new Set();
    this.connStateCbs = new Set();
    this.connState = 'idle';
    this.timestamps = [];
    this.recentEvents = [];
  }

  start() {
    if (this.es) return;
    this._setConnState('connecting');
    this.es = new EventSource('/api-zapd/hls-events');
    this.es.addEventListener('open', () => this._setConnState('open'));
    this.es.addEventListener('error', () => this._setConnState('error'));
    this.es.onmessage = (m) => {
      let evt;
      try {
        evt = JSON.parse(m.data);
      } catch (_) {
        return;
      }
      this._dispatch(evt);
    };
  }

  stop() {
    if (this.es) {
      this.es.close();
      this.es = null;
    }
    this.subscribers.clear();
    this.connStateCbs.clear();
    this.recentEvents.length = 0;
    this.timestamps.length = 0;
    this._setConnState('idle');
  }

  /**
   * @param {(evt:object)=>boolean} filter
   * @param {(evt:object)=>void} handler
   * @returns {()=>void} unsubscribe
   */
  subscribe(filter, handler) {
    const tup = { filter, handler };
    this.subscribers.add(tup);
    return () => this.subscribers.delete(tup);
  }

  onConnState(cb) {
    this.connStateCbs.add(cb);
    cb(this.connState);
    return () => this.connStateCbs.delete(cb);
  }

  getRatePerMinute() {
    const cutoff = Date.now() - RATE_WINDOW_MS;
    while (this.timestamps.length && this.timestamps[0] < cutoff) {
      this.timestamps.shift();
    }
    return this.timestamps.length;
  }

  getRecentEvents() {
    return this.recentEvents.slice();
  }

  _dispatch(evt) {
    this.timestamps.push(Date.now());
    if (this.timestamps.length > 10_000) this.timestamps.splice(0, 1000);
    this.recentEvents.unshift(evt);
    if (this.recentEvents.length > RECENT_EVENTS_CAP) {
      this.recentEvents.length = RECENT_EVENTS_CAP;
    }
    for (const { filter, handler } of this.subscribers) {
      let matches = false;
      try {
        matches = filter(evt);
      } catch (_) {}
      if (matches) {
        try {
          handler(evt);
        } catch (e) {
          console.error('hlsBus handler error', e);
        }
      }
    }
  }

  _setConnState(state) {
    this.connState = state;
    for (const cb of this.connStateCbs) {
      try {
        cb(state);
      } catch (_) {}
    }
  }
}

export const hlsBus = new HlsEventBus();
